import React, { useState, useEffect, useRef, useMemo } from "react";
import Chart from "react-apexcharts";
import api from "../utils/api";
import ChartBurgerMenu from "./ChartBurgerMenu";

function parseTimeStrToHours(timeStr) {
  if (!timeStr) return 0;
  let hours = 0;
  const hrMatch = timeStr.match(/(\d+)\s*hr/i);
  if (hrMatch) hours += parseInt(hrMatch[1], 10);
  const minMatch = timeStr.match(/(\d+)\s*min/i);
  if (minMatch) hours += parseInt(minMatch[1], 10) / 60;
  return Number(hours.toFixed(2));
}

const OnOffChart = ({ username, dateRange, machinesList }) => {
  const [activeTab, setActiveTab] = useState("peakOffPeak");
  const [onOffData, setOnOffData] = useState([]);
  const [peakOffPeakData, setPeakOffPeakData] = useState([]);
  const [loadingOnOff, setLoadingOnOff] = useState(false);
  const [loadingPeakOffPeak, setLoadingPeakOffPeak] = useState(false);
  const [errorOnOff, setErrorOnOff] = useState(null);
  const [errorPeakOffPeak, setErrorPeakOffPeak] = useState(null);
  const chartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hierarchyTree, setHierarchyTree] = useState(null);

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [hoverTip, setHoverTip] = useState(null); // { x, y, row }
  const [legendHover, setLegendHover] = useState(null); // "peak" | "offPeak" | null

  // Fetch the real hierarchy tree (same source as Live tab) so the
  // Peak/Off-Peak view can render a parent-child table like the Live table.
  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    api
      .get(`/hierarchy-tree`, { params: { username } })
      .then((res) => {
        if (!cancelled) setHierarchyTree(res.data || null);
      })
      .catch((err) => {
        console.warn("OnOffChart: hierarchy-tree fetch failed", err);
        if (!cancelled) setHierarchyTree(null);
      });
    return () => {
      cancelled = true;
    };
  }, [username]);

  const toggleRow = (name) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement === chartRef.current ||
        document.webkitFullscreenElement === chartRef.current
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Format Date object to YYYY-MM-DD HH:mm:ss
  const formatToSQL = (date) => {
    if (!date) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const d = new Date(date);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  useEffect(() => {
    const fetchOnOffData = async () => {
      if (!username || !dateRange?.start || !dateRange?.end) return;

      setLoadingOnOff(true);
      setErrorOnOff(null);

      try {
        const start = formatToSQL(dateRange.start);
        const end = formatToSQL(dateRange.end);

        const response = await api.get(`/machine-onoff`, {
          params: {
            username,
            start_datetime: start,
            end_datetime: end,
          },
        });

        if (response.data && response.data.status === "success" && response.data.data) {
          const rawData = response.data.data;
          const totalApiHours = response.data.total_hours || 0;

          const parsed = Object.keys(rawData).map((machine) => {
            const info = rawData[machine];
            let onHours = parseTimeStrToHours(info.on_time);
            let offHours = parseTimeStrToHours(info.off_time);
            let offTimeRaw = info.off_time;

            if (onHours + offHours < totalApiHours) {
              offHours = Number((totalApiHours - onHours).toFixed(2));
              // Let user know the tooltip value was adjusted to meet the total_hours
              offTimeRaw = `${offHours} hrs`;
            }

            return {
              machine,
              onHours,
              offHours,
              onTimeRaw: info.on_time,
              offTimeRaw,
              onoff: info.onoff,
              offon: info.offon,
            };
          });
          // Sort the machines based on maximum on hours (descending)
          parsed.sort((a, b) => b.onHours - a.onHours);
          setOnOffData(parsed);
        } else {
          setOnOffData([]);
        }
      } catch (err) {
        console.error("Error fetching machine ON/OFF data:", err);
        setErrorOnOff("Failed to fetch machine ON/OFF data");
      } finally {
        setLoadingOnOff(false);
      }
    };

    fetchOnOffData();
  }, [username, dateRange]);

  useEffect(() => {
    const fetchPeakOffPeakData = async () => {
      if (!username || !dateRange?.start || !dateRange?.end) return;

      setLoadingPeakOffPeak(true);
      setErrorPeakOffPeak(null);

      try {
        const start = formatToSQL(dateRange.start);
        const end = formatToSQL(dateRange.end);

        const response = await api.get(`/machine-peak-offpeak`, {
          params: {
            username,
            start_datetime: start,
            end_datetime: end,
          },
        });

        if (response.data && response.data.status === "success" && response.data.data) {
          const rawData = response.data.data;
          const parsed = Object.keys(rawData).map((machine) => {
            const info = rawData[machine] || {};
            const peakUnits = Number(info.peak_units || 0);
            const offPeakUnits = Number(info.off_peak_units || 0);
            return {
              machine,
              peakUnits: Number(peakUnits.toFixed(2)),
              offPeakUnits: Number(offPeakUnits.toFixed(2)),
            };
          });

          parsed.sort(
            (a, b) =>
              b.peakUnits + b.offPeakUnits - (a.peakUnits + a.offPeakUnits),
          );
          setPeakOffPeakData(parsed);
        } else {
          setPeakOffPeakData([]);
        }
      } catch (err) {
        console.error("Error fetching machine Peak/Off-Peak data:", err);
        setErrorPeakOffPeak("Failed to fetch machine Peak/Off-Peak data");
      } finally {
        setLoadingPeakOffPeak(false);
      }
    };

    fetchPeakOffPeakData();
  }, [username, dateRange]);

  const isPeakOffPeakTab = activeTab === "peakOffPeak";
  const activeData = isPeakOffPeakTab ? peakOffPeakData : onOffData;
  const activeLoading = isPeakOffPeakTab ? loadingPeakOffPeak : loadingOnOff;
  const activeError = isPeakOffPeakTab ? errorPeakOffPeak : errorOnOff;
  
  const displayData = useMemo(() => {
    if (!activeData || activeData.length === 0) return [];

    // Prioritized sorting: GRID (0) > GENERATOR (1) > MACHINE (2) > Others (3)
    const sorted = [...activeData].sort((a, b) => {
      const getPriority = (name) => {
        const m = (machinesList || []).find((item) => item.name === name);
        const cat = (m?.category || "").toUpperCase();
        if (cat === "GRID") return 0;
        if (cat === "GENERATOR") return 1;
        if (cat === "MACHINE") return 2;
        return 3;
      };

      const pA = getPriority(a.machine);
      const pB = getPriority(b.machine);

      if (pA !== pB) return pA - pB;

      if (isPeakOffPeakTab) {
        const valA = (a.peakUnits || 0) + (a.offPeakUnits || 0);
        const valB = (b.peakUnits || 0) + (b.offPeakUnits || 0);
        return valB - valA;
      } else {
        return (b.onHours || 0) - (a.onHours || 0);
      }
    });

    if (!isFullscreen && sorted.length > 10) {
      return sorted.slice(0, 10);
    }
    return sorted;
  }, [activeData, isFullscreen, machinesList, isPeakOffPeakTab]);

  // ===== Hierarchy table (works for both Peak/Off-Peak and ON/OFF tabs) =====
  // Tab-specific colors / labels for the two stacked segments (A + B).
  const tableConfig = isPeakOffPeakTab
    ? { aColor: "#e11d48", bColor: "#1e3a4e", aLabel: "Peak", bLabel: "Off-Peak", unit: "kWh", header: "Energy [kWh]" }
    : { aColor: "#4caf50", bColor: "#f44336", aLabel: "Hours ON", bLabel: "Hours OFF", unit: "hrs", header: "Hours" };

  const tableDataMap = useMemo(() => {
    const m = new Map();
    if (isPeakOffPeakTab) {
      (peakOffPeakData || []).forEach((d) => {
        m.set(d.machine, { a: d.peakUnits || 0, b: d.offPeakUnits || 0 });
      });
    } else {
      (onOffData || []).forEach((d) => {
        m.set(d.machine, { a: d.onHours || 0, b: d.offHours || 0 });
      });
    }
    return m;
  }, [isPeakOffPeakTab, peakOffPeakData, onOffData]);

  // Flattened, expand-aware rows built from the hierarchy tree.
  // A node shows its own metered value if present, else the sum of its subtree.
  const tableRows = useMemo(() => {
    if (!hierarchyTree) return [];

    const getKids = (node) => {
      const ch = node?.children;
      if (!ch) return [];
      return Array.isArray(ch) ? ch : Object.values(ch);
    };

    const computeAgg = (node) => {
      const own = tableDataMap.get(node.machine_name);
      if (own) return { a: own.a, b: own.b };
      let a = 0;
      let b = 0;
      getKids(node).forEach((c) => {
        const x = computeAgg(c);
        a += x.a;
        b += x.b;
      });
      return { a, b };
    };

    const rows = [];
    const walk = (node, depth) => {
      const kids = getKids(node);
      const name = node.machine_name;
      if (name) {
        const agg = computeAgg(node);
        rows.push({
          name,
          depth,
          category: (node.category || "").toUpperCase(),
          hasChildren: kids.length > 0,
          valA: agg.a,
          valB: agg.b,
          total: agg.a + agg.b,
        });
      }
      if (kids.length > 0 && (!name || expandedRows.has(name))) {
        kids.forEach((c) => walk(c, depth + 1));
      }
    };

    // Skip the client root node — render its children as top-level rows.
    const roots =
      hierarchyTree?.children
        ? Array.isArray(hierarchyTree.children)
          ? hierarchyTree.children
          : Object.values(hierarchyTree.children)
        : [];
    roots.forEach((r) => walk(r, 0));
    return rows;
  }, [hierarchyTree, tableDataMap, expandedRows]);

  const maxRowTotal = useMemo(
    () => tableRows.reduce((m, r) => Math.max(m, r.total), 0),
    [tableRows],
  );

  // Default: expand every parent node when the tree first loads.
  useEffect(() => {
    if (!hierarchyTree) return;
    const names = new Set();
    const getKids = (node) => {
      const ch = node?.children;
      if (!ch) return [];
      return Array.isArray(ch) ? ch : Object.values(ch);
    };
    const walk = (node) => {
      const kids = getKids(node);
      if (kids.length > 0 && node.machine_name) names.add(node.machine_name);
      kids.forEach(walk);
    };
    walk(hierarchyTree);
    setExpandedRows(names);
  }, [hierarchyTree]);

  const series = [
    ...(isPeakOffPeakTab
      ? [
          {
            name: "Peak",
            data: displayData.map((d) => d.peakUnits),
          },
          {
            name: "Off-Peak",
            data: displayData.map((d) => d.offPeakUnits),
          },
        ]
      : [
          {
            name: "Hours ON",
            data: displayData.map((d) => d.onHours),
          },
          {
            name: "Hours OFF",
            data: displayData.map((d) => d.offHours),
          },
        ]),
  ];

  const options = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: {
        show: false,
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    colors: isPeakOffPeakTab ? ["#e11d48", "#1e3a4e"] : ["#4caf50", "#f44336"],
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "60%",
      },
    },
    grid: {
      padding: {
        left: 0,
        right: 0,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: displayData.map((d) => d.machine),
      title: {
        text: isPeakOffPeakTab ? "Energy [kWh]" : "Hours ON",
        style: {
          fontWeight: 600,
          color: "#475569",
        },
      },
    },
    yaxis: {
      title: {
        text: "",
        style: {
          fontWeight: 600,
          color: "#475569",
        },
      },
      labels: {
        minWidth: 105,
        maxWidth: 105,
        style: {
          fontSize: "12px",
          fontFamily: "inherit",
          colors: "#64748b",
        },
      },
    },
    legend: {
      position: "bottom",
      markers: {
        radius: 12,
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const item = displayData[dataPointIndex];
        if (!item) return "";

        if (isPeakOffPeakTab) {
          return `
            <div style="padding: 12px; font-family: inherit; font-size: 13px; min-width: 150px;">
              <div style="font-weight: 700; margin-bottom: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
                ${item.machine}
              </div>
              <div style="color: #be123c; font-weight: 600; margin-bottom: 4px;">
                <span style="display:inline-block; width: 10px; height: 10px; background-color: #e11d48; border-radius: 50%; margin-right: 6px;"></span>
                Peak: ${item.peakUnits.toFixed(2)} kWh
              </div>
              <div style="color: #1e3a4e; font-weight: 600; margin-bottom: 2px;">
                <span style="display:inline-block; width: 10px; height: 10px; background-color: #1e3a4e; border-radius: 50%; margin-right: 6px;"></span>
                Off-Peak: ${item.offPeakUnits.toFixed(2)} kWh
              </div>
            </div>
          `;
        }

        const total = item.onHours + item.offHours;
        const onPct = total > 0 ? Math.round((item.onHours / total) * 100) : 0;
        const offPct = total > 0 ? Math.round((item.offHours / total) * 100) : 0;

        return `
          <div style="padding: 12px; font-family: inherit; font-size: 13px; min-width: 150px;">
            <div style="font-weight: 700; margin-bottom: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
              ${item.machine}
            </div>
            <div style="color: #15803d; font-weight: 600; margin-bottom: 4px;">
              <span style="display:inline-block; width: 10px; height: 10px; background-color: #4caf50; border-radius: 50%; margin-right: 6px;"></span>
              Hours ON: ${item.onTimeRaw} (${onPct}%)
            </div>
            <div style="color: #b91c1c; font-weight: 600; margin-bottom: 8px;">
              <span style="display:inline-block; width: 10px; height: 10px; background-color: #f44336; border-radius: 50%; margin-right: 6px;"></span>
              Hours OFF: ${item.offTimeRaw} (${offPct}%)
            </div>
            <div style="background: #f8fafc; padding: 6px; border-radius: 4px; border: 1px solid #e2e8f0;">
              <div style="color: #475569; font-size: 12px; font-weight: 600; margin-bottom: 4px;">Machine State</div>
              <div style="color: #475569; font-size: 12px;">• ${item.machine} ON to OFF: ${item.onoff} times</div>
              <div style="color: #475569; font-size: 12px;">• ${item.machine} OFF to ON: ${item.offon} times</div>
            </div>
          </div>
        `;
      },
    },
  };

  const chartDataExport = {
    labels: displayData.map((d) => d.machine),
    datasets: isPeakOffPeakTab
      ? [
          { label: "Peak", data: displayData.map((d) => d.peakUnits) },
          { label: "Off-Peak", data: displayData.map((d) => d.offPeakUnits) },
        ]
      : [
          { label: "Hours ON", data: displayData.map((d) => d.onHours) },
          { label: "Hours OFF", data: displayData.map((d) => d.offHours) },
        ],
    unit: isPeakOffPeakTab ? "kWh" : "hrs",
  };

  return (
    <div 
      ref={chartRef} 
      style={{ 
        backgroundColor: "#fff", 
        borderRadius: "16px", 
        border: "1px solid #e2e8f0", 
        overflow: "visible", 
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        display: isFullscreen ? "flex" : "block",
        flexDirection: "column",
        width: isFullscreen ? "100%" : "auto",
        height: isFullscreen ? "100%" : "auto"
      }}
    >
      {hoverTip && (
        <div
          style={{
            position: "fixed",
            left: hoverTip.x + 14,
            top: hoverTip.y + 14,
            zIndex: 2000,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            boxShadow: "0 6px 18px rgba(15,23,42,0.15)",
            padding: 12,
            fontSize: 13,
            minWidth: 160,
            pointerEvents: "none",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8, borderBottom: "1px solid #e2e8f0", paddingBottom: 6 }}>
            {hoverTip.row.name}
          </div>
          <div style={{ color: tableConfig.aColor, fontWeight: 600, marginBottom: 4 }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: tableConfig.aColor, borderRadius: "50%", marginRight: 6 }} />
            {tableConfig.aLabel}: {hoverTip.row.valA.toFixed(2)} {tableConfig.unit}
          </div>
          <div style={{ color: tableConfig.bColor, fontWeight: 600 }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: tableConfig.bColor, borderRadius: "50%", marginRight: 6 }} />
            {tableConfig.bLabel}: {hoverTip.row.valB.toFixed(2)} {tableConfig.unit}
          </div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ fontSize: "18px", fontWeight: 700, color: "#000" }}>
            Productivity
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            onClick={() => setActiveTab("peakOffPeak")}
            style={{
              border: "none",
              borderRadius: "6px",
              padding: "8px 14px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              background: isPeakOffPeakTab ? "#0ea5e9" : "transparent",
              color: isPeakOffPeakTab ? "#fff" : "#0369a1",
            }}
          >
            Peak/ Off-Peak
          </button>
          <button
            onClick={() => setActiveTab("onOff")}
            style={{
              border: "none",
              borderRadius: "6px",
              padding: "8px 14px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              background: !isPeakOffPeakTab ? "#0ea5e9" : "transparent",
              color: !isPeakOffPeakTab ? "#fff" : "#0369a1",
            }}
          >
            ON/OFF
          </button>
          </div>
        </div>
        <ChartBurgerMenu 
          chartRef={chartRef} 
          chartTitle={isPeakOffPeakTab ? "Peak_OffPeak_Chart" : "On_Off_Chart"}
          chartData={displayData.length > 0 ? chartDataExport : null} 
          exportContainer={true} 
          chartType="onoff"
        />
      </div>
      {activeLoading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
          {isPeakOffPeakTab ? "Loading Peak/Off-Peak chart data..." : "Loading ON/OFF chart data..."}
        </div>
      ) : activeError ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#ef4444" }}>{activeError}</div>
      ) : tableRows.length > 0 ? (
        <div style={{ padding: "8px 16px 20px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", maxHeight: isFullscreen ? "none" : 450 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ textAlign: "left", padding: "10px 12px", color: "#475569", fontWeight: 700, position: "sticky", top: 0, background: "#fff", width: "1%", whiteSpace: "nowrap" }}>Machine</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", color: "#475569", fontWeight: 700, position: "sticky", top: 0, background: "#fff" }}>{tableConfig.header}</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr
                    key={`${row.name}-${row.depth}`}
                    style={{
                      borderBottom: "1px solid #f1f5f9",
                      background: row.hasChildren ? "#f8fafc" : "#fff",
                    }}
                  >
                    <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", alignItems: "center", paddingLeft: row.depth * 18 }}>
                        {row.hasChildren ? (
                          <button
                            type="button"
                            onClick={() => toggleRow(row.name)}
                            style={{
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              padding: 0,
                              marginRight: 6,
                              width: 16,
                              color: "#64748b",
                              fontSize: 11,
                            }}
                            aria-label={expandedRows.has(row.name) ? "Collapse" : "Expand"}
                          >
                            {expandedRows.has(row.name) ? "▼" : "▶"}
                          </button>
                        ) : (
                          <span style={{ display: "inline-block", width: 16, marginRight: 6 }} />
                        )}
                        <span style={{ fontWeight: row.hasChildren ? 700 : 500, color: "#0f172a" }}>
                          {row.name}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <div
                        style={{
                          height: 16,
                          background: "#f1f5f9",
                          borderRadius: 4,
                          overflow: "hidden",
                          display: "flex",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) =>
                          setHoverTip({ x: e.clientX, y: e.clientY, row })
                        }
                        onMouseMove={(e) =>
                          setHoverTip({ x: e.clientX, y: e.clientY, row })
                        }
                        onMouseLeave={() => setHoverTip(null)}
                      >
                        {maxRowTotal > 0 && (
                          <div
                            style={{
                              width: `${(row.total / maxRowTotal) * 100}%`,
                              height: "100%",
                              display: "flex",
                            }}
                          >
                            <div
                              style={{
                                width: row.total > 0 ? `${(row.valA / row.total) * 100}%` : "0%",
                                background: tableConfig.aColor,
                                opacity: legendHover === "b" ? 0.2 : 1,
                                transition: "opacity 0.15s",
                              }}
                            />
                            <div
                              style={{
                                width: row.total > 0 ? `${(row.valB / row.total) * 100}%` : "0%",
                                background: tableConfig.bColor,
                                opacity: legendHover === "a" ? 0.2 : 1,
                                transition: "opacity 0.15s",
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 20, padding: "12px 0 0", fontSize: 12, color: "#475569" }}>
            <span
              onMouseEnter={() => setLegendHover("a")}
              onMouseLeave={() => setLegendHover(null)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                opacity: legendHover === "b" ? 0.4 : 1,
                transition: "opacity 0.15s",
              }}
            >
              <span style={{ width: 12, height: 12, borderRadius: 3, background: tableConfig.aColor, display: "inline-block" }} />
              {tableConfig.aLabel}
            </span>
            <span
              onMouseEnter={() => setLegendHover("b")}
              onMouseLeave={() => setLegendHover(null)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                opacity: legendHover === "a" ? 0.4 : 1,
                transition: "opacity 0.15s",
              }}
            >
              <span style={{ width: 12, height: 12, borderRadius: 3, background: tableConfig.bColor, display: "inline-block" }} />
              {tableConfig.bLabel}
            </span>
          </div>
        </div>
      ) : activeData.length > 0 ? (
        <div style={{ padding: "0 8px 20px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, minHeight: 0, overflowY: isFullscreen ? "auto" : "visible", position: "relative", paddingLeft: "0" }}>
            <div
              style={{
                position: "absolute",
                left: "0",
                top: "50%",
                transform: "translateY(-50%) rotate(180deg)",
                transformOrigin: "center",
                writingMode: "vertical-lr",
                fontWeight: 600,
                color: "#475569",
                fontSize: "14px",
                pointerEvents: "none",
              }}
            >
              Machines
            </div>
            <Chart options={options} series={series} type="bar" height={isFullscreen ? Math.max(window.innerHeight - 100, displayData.length * 50) : 450} width="100%" />
          </div>
          {!isFullscreen && activeData.length > 10 && (
            <div style={{
              textAlign: "center",
              padding: "10px",
              fontSize: "12px",
              color: "#64748b",
              background: "#f8fafc",
              borderTop: "1px solid #f1f5f9",
              borderRadius: "0 0 16px 16px",
              fontWeight: 500
            }}>
              Showing top 10 machines. <span style={{ color: "#0ea5e9", fontWeight: 700 }}>Open full screen</span> to view all {activeData.length} machines.
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
          {isPeakOffPeakTab
            ? "No Peak/Off-Peak data available for the selected period."
            : "No ON/OFF data available for the selected period."}
        </div>
      )}
    </div>
  );
};

export default OnOffChart;
